import { Field, Modal, ActionButton, Autocomplete, TextField, Text } from 'components/commons';
import { useApi, useForm, useModal } from 'hooks';
import React, { useCallback, useMemo } from 'react';
import { initialState as formState } from './transaction-form.state';
import styles from './transaction.module.scss';
import { NotifModal } from 'components/modals';
import { Grid } from '@material-ui/core';
import locale from 'localization';
import { useEffect } from 'react';
import { CarWithLady } from 'images';
import { formatAmount, handleRequest, parseNumber } from 'utils';
import SelectProduct from 'components/field/select-product/select-product';
import SelectCustomer from 'components/field/select-customer/select-customer';
import { createTransaction, updateTransaction } from 'apis/transaction.api';
import { InputAmount } from 'components/field';
import { getCustomerAccountByNumber } from 'apis/customer.api';

const TransactionForm = ({
  refetchTransactions,
  initialState,
  actionText,
  create,
  customerId,
  firstName,
  lastName,
  refreshPage,
  product,
  customerAccountNumber,
  ...state
}) => {
  const { close } = state;
  const notifModal = useModal();
  const confirmTransactionModal = useModal();
  const form = useMemo(() => {
    return formState(initialState);
  }, [initialState]);

  const { fields, modifyField, submitForm, getFormValues, clearForm, validateField, isFormSubmittable, applyFieldErrors } =
    useForm({
      initialState: form
    });

  useEffect(() => {
    if (state.active) {
      fetchCustomerAccountRequest();
      clearForm();
    }
    //eslint-disable-next-line
  }, [state.active]);

  const {
    request: getCustomerAccountRequest,
    loading: fetchingCustomerAccount,
    result: getCustomerAccountResult = { customerAccount: {} }
  } = useApi({
    api: getCustomerAccountByNumber,
    pageError: false,
    params: {
      customerAccountNumber
    }
  });

  const fetchCustomerAccountRequest = useCallback(() => {
    getCustomerAccountRequest();
  }, [getCustomerAccountRequest]);

  const addTransactionRequest = useApi({
    api: createTransaction,
    handleOwnError: {
      badrequest: true
    }
  });

  const editCustomerRequest = useApi({
    api: updateTransaction,
    handleOwnError: {
      badrequest: true
    },
    params: {
      customerId
    }
  });

  const loading = addTransactionRequest.loading || editCustomerRequest.loading;

  const submit = async () => {
    const params = getFormValues();

    const apiRequest = create ? addTransactionRequest : editCustomerRequest;

    if (create) {
      params.customerId = params.customerId ? params.customerId : customerId;
    }

    params.userId = Number(localStorage.getItem('userId'));
    params.amount = parseNumber(params.amount);

    handleRequest(
      async () => {
        const finalParams = {
          ...params
        };
        await apiRequest.request(finalParams, () => submit());

        close();
        refetchTransactions();
        notifModal.show({
          image: CarWithLady,
          title: create ? 'Transaction created' : locale.customerAccountSuccessEdited,
          contentHighlight: [<b>{formatAmount(params.amount)}</b>, <b>{params.type}</b>],
          content: create ? locale.successfullyCreatedTransaction : locale.successfullyChangeInfoOfCustomerName
        });
      },
      {
        CA0001: () => {
          applyFieldErrors({
            productId: 'Customer account not found'
          });
        },
        CA0005: () => {
          applyFieldErrors({
            amount: 'Insufficient account balance'
          });
        },
        CA0009: () => {
          applyFieldErrors({
            amount: 'Customer account is not active'
          });
        }
      }
    );
  };

  let tranCodes = [
    {
      label: 'OR',
      value: 'OR'
    },
    {
      label: 'CL',
      value: 'CL'
    },
    {
      label: 'BDY',
      value: 'BDY'
    },
    {
      label: 'PYL',
      value: 'PYL'
    }
  ];

  if (product === 'shared-capital') {
    tranCodes = [
      ...tranCodes,
      {
        label: 'DIV',
        value: 'DIV'
      }
    ];
  } else if (product === 'savings') {
    tranCodes = [
      {
        label: 'OR',
        value: 'OR'
      },
      {
        label: 'BDY',
        value: 'BDY'
      },
      {
        label: 'PYL',
        value: 'PYL'
      }
    ];
  }

  return (
    <div>
      <NotifModal {...notifModal} />
      <NotifModal {...confirmTransactionModal} />
      <Modal close={loading ? false : close} {...state} className={styles.modalContent}>
        <div className={styles.formContent}>
          <div className={styles.content}>
            <Grid container direction="row" spacing={2}>
              <Grid item xs={12} sm={6}>
                <Field {...fields.customerId} label="CIF Number">
                  <SelectCustomer {...fields.customerId} onChange={modifyField} disabled />
                </Field>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Field label="Transaction Type">
                  <Autocomplete
                    name="type"
                    value={fields.type}
                    options={[
                      {
                        label: 'Deposit',
                        value: 'Deposit'
                      },
                      {
                        label: 'Withdrawal',
                        value: 'Withdrawal'
                      }
                    ]}
                    {...fields.type}
                    onChange={modifyField}
                  />
                </Field>
              </Grid>
            </Grid>
            <Grid container direction="row" spacing={2}>
              <Grid item xs={12} sm={6}>
                <Field className={styles.status} label="Product">
                  <SelectProduct {...fields.productId} onChange={modifyField} disabled />
                </Field>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Field {...fields.tranCode}>
                  <Autocomplete
                    name="tranCode"
                    value={fields.tranCode}
                    options={tranCodes}
                    {...fields.tranCode}
                    onChange={modifyField}
                  />
                </Field>
              </Grid>
            </Grid>
            <Grid container direction="row" spacing={2}>
              <Grid item xs={12} sm={6}>
                <Field {...fields.amount}>
                  <InputAmount {...fields.amount} innerLabel onChange={modifyField} />
                </Field>
              </Grid>
            </Grid>
            <Grid container direction="row" spacing={2}>
              <Grid item xs={12} sm={10}>
                <Field {...fields.remarks}>
                  <TextField {...fields.remarks} onChange={modifyField} onBlur={validateField} />
                </Field>
              </Grid>
            </Grid>

            {!fetchingCustomerAccount && (
              <Grid container direction="row" spacing={2}>
                <Grid item xs={12} sm={10}>
                  <Text bolder>Account Balance: {formatAmount(getCustomerAccountResult?.customerAccount?.balance ?? 0)}</Text>
                </Grid>
              </Grid>
            )}
          </div>
        </div>
        <ActionButton
          right
          items={[
            {
              onClick: () => {
                state.close();
              },
              disabled: loading,
              text: locale.cancel
            },
            {
              onClick: () => {
                const { customerAccount } = getCustomerAccountResult;
                if (customerAccount && !fetchingCustomerAccount) {
                  let actions = {};
                  let actionTitle = '';
                  let actionContent = '';
                  if (fields.type.value === 'Withdrawal') {
                    if (product === 'fixed-deposit' || product === 'shared-capital') {
                      if (parseNumber(fields.amount.value) === customerAccount.balance) {
                        actionTitle = 'Withdrawal Confirmation - Account Termination';
                        actionContent = `Withdrawing your balance before end of term will result to account termination. Would you like to proceed?"`;
                        actions = {
                          primary: {
                            text: 'Yes',
                            onClick: () => {
                              confirmTransactionModal.close();
                              submitForm(submit);
                            }
                          },
                          secondary: {
                            text: 'No',
                            onClick: () => {
                              confirmTransactionModal.close();
                            }
                          }
                        };
                      } else if (parseNumber(fields.amount.value) > customerAccount.balance) {
                        applyFieldErrors({
                          amount: 'Insufficient account balance'
                        });

                        return;
                      } else {
                        actionTitle = 'Withdrawal Warning - Partial';
                        actionContent = `Partial withdrawal is not allowed. Please withdraw the full amount to terminate the account.`;
                        actions = {
                          primary: {
                            text: 'Got it',
                            onClick: () => {
                              confirmTransactionModal.close();
                            }
                          }
                        };
                      }

                      confirmTransactionModal.show({
                        title: actionTitle,
                        content: actionContent,
                        contentHighlight: [<b>{fields.amount.value}</b>, <b>{customerAccount.customerAccountNumber}</b>],
                        ...actions
                      });
                    } else {
                      submitForm(submit);
                    }
                  } else {
                    submitForm(submit);
                  }
                }
              },
              disabled: !isFormSubmittable,
              loading: loading || fetchingCustomerAccount,
              text: actionText
            }
          ]}
        />
      </Modal>
    </div>
  );
};

export default TransactionForm;
